import React, { useCallback, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';
import LyricConfirm from '../components/LyricConfirm';

/**
 * Admin → AWS Logs Access page.
 *
 * A test harness for OPS-4520's cross-account `svc-lyric-x-account-readonly`
 * role (deploy/cloudformation/cfn-lyric-x-account-readonly-role.yaml): lets
 * an admin verify — from the browser, with real (pod IRSA) credentials —
 * that Lyric can assume the role in a given environment/account and read
 * CloudWatch Logs, BEFORE any deployed-app-facing API is built on top of it.
 *
 * Never displays or receives the assumed temporary AWS credentials; the
 * server only reports success/failure plus a small proof (a few log group
 * names). Every test attempt is written to a persisted audit log (mirrors
 * the "JIRA → Salesforce" admin tool's log), viewable below.
 */

// Mask everything but a short prefix/suffix so a screen-share doesn't leak
// the working key, mirroring GoogleDocsApiModal's convention.
function maskKey(key) {
    if (!key) return '';
    return `${key.slice(0, 9)}${'•'.repeat(Math.max(0, key.length - 13))}${key.slice(-4)}`;
}

// CloudWatch Logs Insights statuses. The service layer now throws on the
// terminal-failure ones (server/services/awsCrossAccountLogsService.js), but
// this is kept defensive so the UI never mislabels a non-Complete status.
const INSIGHTS_RUNNING_STATUSES = new Set(['Running', 'Scheduled']);
const INSIGHTS_FAILURE_STATUSES = new Set(['Failed', 'Cancelled', 'Timeout']);

// Rows can each omit fields that weren't present for that particular log
// event, so the column set must be the union across ALL rows, not just the
// first one, or later-row-only fields would be silently dropped.
function insightsColumns(results) {
    const seen = new Set();
    const columns = [];
    for (const row of (results || [])) {
        for (const field of Object.keys(row)) {
            if (!seen.has(field)) {
                seen.add(field);
                columns.push(field);
            }
        }
    }
    return columns.length ? columns : ['(no fields)'];
}

function appUsageSnippet() {
    return `const key = process.env.LYRIC_AWS_LOGS_API_KEY;
// LYRIC_AWS_LOGS_API_BASE_URL is auto-injected alongside the key -- your app
// runs on a different origin than Lyric, so a relative '/api/...' URL would
// hit YOUR app's own server instead of Lyric's.
const baseUrl = process.env.LYRIC_AWS_LOGS_API_BASE_URL || '';
const headers = { Authorization: 'Bearer ' + key, 'Content-Type': 'application/json' };

// discover real log group names first
await fetch(baseUrl + '/api/aws-logs-api/log-groups?prefix=/aws/lambda/my-service', { headers });

// then fetch events (simple FilterLogEvents-style filtering)
await fetch(baseUrl + '/api/aws-logs-api/query', {
  method: 'POST', headers,
  body: JSON.stringify({ logGroupName: '/aws/lambda/my-service', filterPattern: 'ERROR', limit: 100 }),
});

// OR, if your existing code already speaks CloudWatch Logs Insights syntax
// (StartQuery/GetQueryResults, e.g. fromTemporaryCredentials()-based Lambda code):
await fetch(baseUrl + '/api/aws-logs-api/insights-query', {
  method: 'POST', headers,
  body: JSON.stringify({
    logGroupName: '/aws/lambda/my-service',
    queryString: 'fields @timestamp, @message | filter @message like /ERROR/ | sort @timestamp desc | limit 100',
    startTime: Math.floor(Date.now() / 1000) - 3600,
    endTime: Math.floor(Date.now() / 1000),
  }),
});`;
}

export default function AdminAwsLogsAccess() {
    const { oktaAuth } = useOktaAuth() || {};
    const [config, setConfig] = useState(null);
    const [environments, setEnvironments] = useState([]); // [{name, accountId, region}]
    const [newEnvName, setNewEnvName] = useState('');
    const [newEnvAccountId, setNewEnvAccountId] = useState('');
    const [newEnvRegion, setNewEnvRegion] = useState('us-west-2');
    const [selectedEnv, setSelectedEnv] = useState('');
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [savingConfig, setSavingConfig] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [error, setError] = useState('');
    const [errorDetail, setErrorDetail] = useState(null);
    const [log, setLog] = useState([]);
    const [loadingLog, setLoadingLog] = useState(false);
    const [queryLogGroup, setQueryLogGroup] = useState('');
    const [queryFilterPattern, setQueryFilterPattern] = useState('');
    const [queryLimit, setQueryLimit] = useState(25);
    const [querying, setQuerying] = useState(false);
    const [queryResult, setQueryResult] = useState(null);
    const [queryError, setQueryError] = useState('');
    const [insightsLogGroup, setInsightsLogGroup] = useState('');
    const [insightsQueryString, setInsightsQueryString] = useState('fields @timestamp, @message | sort @timestamp desc | limit 20');
    const [insightsLookbackMinutes, setInsightsLookbackMinutes] = useState(60);
    const [insightsQuerying, setInsightsQuerying] = useState(false);
    const [insightsResult, setInsightsResult] = useState(null);
    const [insightsError, setInsightsError] = useState('');

    // Per-app "authenticate access" management (enable/disable/rotate the
    // developer-facing /api/aws-logs-api/* key for one deployed app).
    const [apps, setApps] = useState([]);
    const [loadingApps, setLoadingApps] = useState(true);
    const [appsError, setAppsError] = useState('');
    const [expandedApp, setExpandedApp] = useState(null);
    const [appStatus, setAppStatus] = useState(null);
    const [loadingAppStatus, setLoadingAppStatus] = useState(false);
    const [appStatusError, setAppStatusError] = useState('');
    const [appBusy, setAppBusy] = useState(false);
    const [appEnvChoice, setAppEnvChoice] = useState('');
    const [revealAppKey, setRevealAppKey] = useState(false);
    const [copiedApp, setCopiedApp] = useState('');
    const [confirmRotateApp, setConfirmRotateApp] = useState(false);
    const [appRedeployNeeded, setAppRedeployNeeded] = useState(false);
    const [redeployingApp, setRedeployingApp] = useState(false);
    const [appRedeployApplied, setAppRedeployApplied] = useState(false);

    const authHeader = useCallback(async () => {
        let token = '';
        try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, [oktaAuth]);

    const loadConfig = useCallback(async () => {
        setLoadingConfig(true);
        setError('');
        setErrorDetail(null);
        try {
            const headers = await authHeader();
            const res = await fetch('/api/admin/aws-logs/config', { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErrorDetail({ status: res.status, ...body });
                throw new Error(body?.error || `HTTP ${res.status}`);
            }
            setConfig(body.config);
            const envs = Object.entries(body.config.environments || {})
                .map(([name, v]) => ({ name, accountId: v.accountId, region: v.region }));
            setEnvironments(envs);
            if (envs.length && !selectedEnv) setSelectedEnv(envs[0].name);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoadingConfig(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authHeader]);

    const loadLog = useCallback(async () => {
        setLoadingLog(true);
        try {
            const headers = await authHeader();
            const res = await fetch('/api/admin/aws-logs/log?limit=50', { headers });
            const body = await res.json().catch(() => ({}));
            if (res.ok) setLog(body.entries || []);
        } catch { /* non-fatal */ } finally {
            setLoadingLog(false);
        }
    }, [authHeader]);

    const loadApps = useCallback(async () => {
        setLoadingApps(true);
        setAppsError('');
        try {
            const headers = await authHeader();
            const res = await fetch('/api/deploy/list', { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setApps(body.apps || []);
        } catch (e) {
            setAppsError(e.message);
        } finally {
            setLoadingApps(false);
        }
    }, [authHeader]);

    useEffect(() => { loadConfig(); loadLog(); loadApps(); }, [loadConfig, loadLog, loadApps]);

    const loadAppStatus = useCallback(async (appName) => {
        setLoadingAppStatus(true);
        setAppStatusError('');
        try {
            const headers = await authHeader();
            const res = await fetch(`/api/apps/${encodeURIComponent(appName)}/aws-logs`, { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setAppStatus(body);
            setAppEnvChoice(body.environment || '');
        } catch (e) {
            setAppStatusError(e.message);
        } finally {
            setLoadingAppStatus(false);
        }
    }, [authHeader]);

    const toggleExpandApp = (appName) => {
        if (expandedApp === appName) {
            setExpandedApp(null);
            setAppStatus(null);
            return;
        }
        setExpandedApp(appName);
        setAppStatus(null);
        setAppStatusError('');
        setRevealAppKey(false);
        setAppRedeployNeeded(false);
        setAppRedeployApplied(false);
        loadAppStatus(appName);
    };

    // path: 'enable' | 'disable' | 'rotate-key'
    const appAct = useCallback(async (path) => {
        if (!expandedApp) return;
        setAppBusy(true);
        setAppStatusError('');
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const body = path === 'enable' ? JSON.stringify({ environment: appEnvChoice }) : undefined;
            const res = await fetch(`/api/apps/${encodeURIComponent(expandedApp)}/aws-logs/${path}`, {
                method: 'POST', headers, body,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            await loadAppStatus(expandedApp);
            if (path === 'disable') {
                setAppRedeployNeeded(false);
                setAppRedeployApplied(false);
            } else {
                if (path === 'rotate-key') setRevealAppKey(true);
                setAppRedeployNeeded(!!data.redeployRequired);
                setAppRedeployApplied(false);
            }
        } catch (e) {
            setAppStatusError(e.message);
        } finally {
            setAppBusy(false);
        }
    }, [expandedApp, appEnvChoice, authHeader, loadAppStatus]);

    const applyAppRedeploy = useCallback(async () => {
        if (!expandedApp) return;
        setRedeployingApp(true);
        setAppStatusError('');
        try {
            const headers = await authHeader();
            const res = await fetch(`/api/apps/${encodeURIComponent(expandedApp)}/aws-logs/apply`, { method: 'POST', headers });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setAppRedeployNeeded(false);
            setAppRedeployApplied(true);
        } catch (e) {
            setAppStatusError(e.message);
        } finally {
            setRedeployingApp(false);
        }
    }, [expandedApp, authHeader]);

    const copyAppText = useCallback(async (text, what) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedApp(what);
            setTimeout(() => setCopiedApp(''), 1500);
        } catch { /* clipboard blocked — user can select manually */ }
    }, []);

    const patchEnvironments = useCallback(async (environmentsPatch) => {
        setSavingConfig(true);
        setError('');
        setErrorDetail(null);
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/admin/aws-logs/config', {
                method: 'PUT',
                headers,
                body: JSON.stringify({ environmentsPatch }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErrorDetail({ status: res.status, ...body });
                throw new Error(body?.error || `HTTP ${res.status}`);
            }
            setConfig(body.config);
            const envs = Object.entries(body.config.environments || {})
                .map(([name, v]) => ({ name, accountId: v.accountId, region: v.region }));
            setEnvironments(envs);
        } catch (e) {
            setError(e.message);
        } finally {
            setSavingConfig(false);
        }
    }, [authHeader]);

    const addEnvironment = () => {
        const name = newEnvName.trim().toLowerCase();
        const accountId = newEnvAccountId.trim();
        if (!name || !/^\d{12}$/.test(accountId)) {
            setError('Environment name and a 12-digit AWS account ID are required.');
            return;
        }
        const region = newEnvRegion || 'us-west-2';
        setNewEnvName(''); setNewEnvAccountId(''); setNewEnvRegion('us-west-2');
        setSelectedEnv(name);
        // Sent as a scoped upsert (not the whole map) so a concurrent admin
        // adding/removing a *different* environment can't be clobbered.
        patchEnvironments({ upsert: { [name]: { accountId, region } } });
    };

    const removeEnvironment = (name) => {
        if (selectedEnv === name) {
            const remaining = environments.filter((e) => e.name !== name);
            setSelectedEnv(remaining.length ? remaining[0].name : '');
        }
        patchEnvironments({ remove: [name] });
    };

    const runTest = useCallback(async () => {
        if (!selectedEnv) {
            setError('Pick an environment to test first.');
            return;
        }
        setTesting(true);
        setError('');
        setErrorDetail(null);
        setTestResult(null);
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/admin/aws-logs/test', {
                method: 'POST',
                headers,
                body: JSON.stringify({ environment: selectedEnv }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErrorDetail({ status: res.status, ...body });
                throw new Error(body?.error || `HTTP ${res.status}`);
            }
            setTestResult(body);
            if (body.logGroups && body.logGroups.length && !queryLogGroup) {
                setQueryLogGroup(body.logGroups[0]);
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setTesting(false);
            loadLog();
        }
    }, [selectedEnv, authHeader, loadLog, queryLogGroup]);

    const runQuery = useCallback(async () => {
        if (!selectedEnv) {
            setQueryError('Pick an environment first.');
            return;
        }
        if (!queryLogGroup) {
            setQueryError('Pick a log group first (run the test above to discover some).');
            return;
        }
        setQuerying(true);
        setQueryError('');
        setQueryResult(null);
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/admin/aws-logs/query', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    environment: selectedEnv,
                    logGroupName: queryLogGroup,
                    filterPattern: queryFilterPattern || undefined,
                    limit: Number(queryLimit) || 25,
                }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(body?.error || `HTTP ${res.status}`);
            }
            setQueryResult(body);
        } catch (e) {
            setQueryError(e.message);
        } finally {
            setQuerying(false);
            loadLog();
        }
    }, [selectedEnv, queryLogGroup, queryFilterPattern, queryLimit, authHeader, loadLog]);

    const runInsightsQuery = useCallback(async () => {
        if (!selectedEnv) {
            setInsightsError('Pick an environment first.');
            return;
        }
        if (!insightsLogGroup) {
            setInsightsError('Pick a log group first (run the test above to discover some).');
            return;
        }
        if (!insightsQueryString.trim()) {
            setInsightsError('An Insights query string is required.');
            return;
        }
        setInsightsQuerying(true);
        setInsightsError('');
        setInsightsResult(null);
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const endTime = Math.floor(Date.now() / 1000);
            const startTime = endTime - Math.max(1, Number(insightsLookbackMinutes) || 60) * 60;
            const res = await fetch('/api/admin/aws-logs/insights-query', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    environment: selectedEnv,
                    logGroupName: insightsLogGroup,
                    queryString: insightsQueryString,
                    startTime,
                    endTime,
                }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(body?.error || `HTTP ${res.status}`);
            }
            setInsightsResult(body);
        } catch (e) {
            setInsightsError(e.message);
        } finally {
            setInsightsQuerying(false);
            loadLog();
        }
    }, [selectedEnv, insightsLogGroup, insightsQueryString, insightsLookbackMinutes, authHeader, loadLog]);

    return (
        <div style={styles.page}>
            <div style={styles.pageHeader}>
                <h2 style={styles.title}>☁️ AWS Logs Access (OPS-4520)</h2>
            </div>
            <p style={styles.subtitle}>
                Test harness for the cross-account <code>{(config && config.roleName) || 'svc-lyric-x-account-readonly'}</code> role.
                Assumes the role in a chosen environment&apos;s account using Lyric&apos;s own pod credentials, then runs one
                read-only <code>DescribeLogGroups</code> call to prove the trust policy + permissions actually work.
                Temporary AWS credentials never leave the server — only success/failure and a few log group names are shown.
            </p>

            <AdminMenu />

            {error && (
                <div style={styles.error}>
                    <div>{error}</div>
                    {errorDetail && errorDetail.currentUser !== undefined && (
                        <div style={styles.errInline}>
                            Signed in as: {errorDetail.currentUser || '(none)'} — {errorDetail.hint}
                        </div>
                    )}
                    {errorDetail && errorDetail.auditLogError && (
                        <div style={styles.errInline}>
                            ⚠️ The audit log entry for this attempt was also NOT recorded: {errorDetail.auditLogError}
                        </div>
                    )}
                </div>
            )}

            <div style={styles.section}>
                <h3 style={styles.h3}>Environments</h3>
                <p style={styles.help}>
                    Map an environment name to the AWS account ID the role was deployed into by DevOps. Not guessed —
                    add each one you have a confirmed account ID for.
                </p>
                {loadingConfig ? <p style={styles.help}>Loading…</p> : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Name</th>
                                <th style={styles.th}>Account ID</th>
                                <th style={styles.th}>Region</th>
                                <th style={styles.th} />
                            </tr>
                        </thead>
                        <tbody>
                            {environments.map((env) => (
                                <tr key={env.name}>
                                    <td style={styles.td}>{env.name}</td>
                                    <td style={styles.td}>{env.accountId}</td>
                                    <td style={styles.td}>{env.region}</td>
                                    <td style={styles.td}>
                                        <button
                                            type="button"
                                            style={styles.btnDanger}
                                            disabled={savingConfig}
                                            onClick={() => removeEnvironment(env.name)}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td style={styles.td}>
                                    <input
                                        style={styles.input}
                                        placeholder="e.g. qa"
                                        value={newEnvName}
                                        onChange={(e) => setNewEnvName(e.target.value)}
                                    />
                                </td>
                                <td style={styles.td}>
                                    <input
                                        style={styles.input}
                                        placeholder="12-digit account ID"
                                        value={newEnvAccountId}
                                        onChange={(e) => setNewEnvAccountId(e.target.value)}
                                    />
                                </td>
                                <td style={styles.td}>
                                    <input
                                        style={styles.input}
                                        value={newEnvRegion}
                                        onChange={(e) => setNewEnvRegion(e.target.value)}
                                    />
                                </td>
                                <td style={styles.td}>
                                    <button type="button" style={styles.btn} disabled={savingConfig} onClick={addEnvironment}>
                                        Add
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                )}
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>Deployed apps — authenticate access</h3>
                <p style={styles.help}>
                    Grant one deployed app its own scoped key to call the developer-facing{' '}
                    <code>/api/aws-logs-api/*</code> relay — bound to exactly one environment above,
                    rate-limited, and fully audited. Lyric auto-injects the key as{' '}
                    <code>LYRIC_AWS_LOGS_API_KEY</code> into the app&apos;s process on its next
                    (re)deploy; the app itself never sees or handles real AWS credentials.
                </p>
                {appsError && <div style={styles.error}>{appsError}</div>}
                {loadingApps ? <p style={styles.help}>Loading apps…</p> : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>App</th>
                                <th style={styles.th}>Owner</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>AWS Logs</th>
                                <th style={styles.th} />
                            </tr>
                        </thead>
                        <tbody>
                            {apps.map((a) => (
                                <React.Fragment key={a.appName}>
                                    <tr>
                                        <td style={styles.td}>{a.displayName || a.appName}</td>
                                        <td style={styles.td}>{a.deployedBy}</td>
                                        <td style={styles.td}>{a.status}</td>
                                        <td style={styles.td}>
                                            {expandedApp === a.appName && appStatus ? (
                                                <span style={appStatus.awsLogsEnabled ? styles.badgeOk : styles.badgeFail}>
                                                    {appStatus.awsLogsEnabled ? 'Enabled' : 'Disabled'}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td style={styles.td}>
                                            <button type="button" style={styles.btn} onClick={() => toggleExpandApp(a.appName)}>
                                                {expandedApp === a.appName ? 'Close' : 'Manage'}
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedApp === a.appName && (
                                        <tr>
                                            <td style={styles.td} colSpan={5}>
                                                {loadingAppStatus ? <p style={styles.help}>Loading…</p> : appStatusError ? (
                                                    <div style={styles.error}>{appStatusError}</div>
                                                ) : appStatus && (
                                                    <div style={styles.tlsBlock}>
                                                        {!appStatus.toolEnabled && (
                                                            <div style={styles.auditWarn}>
                                                                ⚠️ The AWS Logs API is globally disabled above (see Environments) —
                                                                enabling here won&apos;t take effect until it&apos;s re-enabled.
                                                            </div>
                                                        )}
                                                        <div style={styles.actions}>
                                                            <select
                                                                style={styles.input}
                                                                value={appEnvChoice}
                                                                onChange={(e) => setAppEnvChoice(e.target.value)}
                                                            >
                                                                <option value="">Select environment…</option>
                                                                {(appStatus.knownEnvironments || []).map((name) => (
                                                                    <option key={name} value={name}>{name}</option>
                                                                ))}
                                                            </select>
                                                            {!appStatus.awsLogsEnabled ? (
                                                                <button
                                                                    type="button"
                                                                    style={styles.btnPrimary}
                                                                    disabled={appBusy || !appEnvChoice}
                                                                    onClick={() => appAct('enable')}
                                                                >
                                                                    {appBusy ? 'Enabling…' : 'Enable AWS Logs API'}
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        style={styles.btn}
                                                                        disabled={appBusy || !appEnvChoice || appEnvChoice === appStatus.environment}
                                                                        onClick={() => appAct('enable')}
                                                                        title="Re-assign this app to a different environment"
                                                                    >
                                                                        Change environment
                                                                    </button>
                                                                    <button type="button" style={styles.btn} disabled={appBusy} onClick={() => setConfirmRotateApp(true)}>
                                                                        Rotate key
                                                                    </button>
                                                                    <button type="button" style={styles.btnDanger} disabled={appBusy} onClick={() => appAct('disable')}>
                                                                        {appBusy ? 'Disabling…' : 'Disable'}
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>

                                                        {appStatus.awsLogsEnabled && appStatus.apiKey && (
                                                            <>
                                                                <div style={{ marginTop: '10px' }}>
                                                                    <div style={styles.help}>
                                                                        API key (auto-injected as <code>LYRIC_AWS_LOGS_API_KEY</code>)
                                                                    </div>
                                                                    <div style={styles.actions}>
                                                                        <code style={styles.codeInline}>
                                                                            {revealAppKey ? appStatus.apiKey : maskKey(appStatus.apiKey)}
                                                                        </code>
                                                                        <button type="button" style={styles.btn} onClick={() => setRevealAppKey((v) => !v)}>
                                                                            {revealAppKey ? 'Hide' : 'Reveal'}
                                                                        </button>
                                                                        <button type="button" style={styles.btn} onClick={() => copyAppText(appStatus.apiKey, 'key')}>
                                                                            {copiedApp === 'key' ? 'Copied!' : 'Copy'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div style={{ marginTop: '10px' }}>
                                                                    <div style={styles.help}>Usage snippet for the developer</div>
                                                                    <div style={styles.actions}>
                                                                        <button type="button" style={styles.btn} onClick={() => copyAppText(appUsageSnippet(), 'curl')}>
                                                                            {copiedApp === 'curl' ? 'Copied!' : 'Copy snippet'}
                                                                        </button>
                                                                    </div>
                                                                    <pre style={styles.pre}>{appUsageSnippet()}</pre>
                                                                </div>
                                                            </>
                                                        )}

                                                        {appRedeployNeeded && (
                                                            <div style={styles.auditWarn}>
                                                                Redeploy <b>{a.displayName || a.appName}</b> to apply the key to the running app.
                                                                <button
                                                                    type="button"
                                                                    style={{ ...styles.btnPrimary, marginLeft: '10px' }}
                                                                    disabled={redeployingApp}
                                                                    onClick={applyAppRedeploy}
                                                                >
                                                                    {redeployingApp ? 'Redeploying…' : 'Redeploy now'}
                                                                </button>
                                                            </div>
                                                        )}
                                                        {appRedeployApplied && !appRedeployNeeded && (
                                                            <div style={styles.help}>🚀 Redeploy started — the key is injected on respawn.</div>
                                                        )}
                                                        {appStatus.rateLimit && (
                                                            <div style={styles.help}>
                                                                Limit: <b>{appStatus.rateLimit.max}</b> / {Math.round((appStatus.rateLimit.windowMs || 0) / 60000)} min
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {!apps.length && (
                                <tr><td style={styles.td} colSpan={5}>No deployed apps found.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>Run test</h3>
                <div style={styles.actions}>
                    <select
                        style={styles.input}
                        value={selectedEnv}
                        onChange={(e) => setSelectedEnv(e.target.value)}
                    >
                        <option value="">Select environment…</option>
                        {environments.map((env) => (
                            <option key={env.name} value={env.name}>{env.name} ({env.accountId})</option>
                        ))}
                    </select>
                    <button type="button" style={styles.btnPrimary} disabled={testing || !selectedEnv} onClick={runTest}>
                        {testing ? 'Testing…' : 'Assume role & list log groups'}
                    </button>
                </div>

                {testResult && (
                    <div style={styles.tlsBlock}>
                        <div><strong style={{ color: '#c8e6c9' }}>✅ Success</strong></div>
                        <div>Role ARN: {testResult.roleArn}</div>
                        <div>Assumed role ID: {testResult.assumedRoleId}</div>
                        <div>Credential expiration: {testResult.expiration}</div>
                        <div>Log groups found ({testResult.logGroups.length}):</div>
                        <ul>
                            {testResult.logGroups.map((lg) => <li key={lg}>{lg}</li>)}
                        </ul>
                        {testResult.auditLogError && (
                            <div style={styles.auditWarn}>
                                ⚠️ This attempt was NOT recorded in the audit log: {testResult.auditLogError}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>Fetch real log data</h3>
                <p style={styles.help}>
                    Goes beyond the permission check above: runs one bounded, read-only <code>FilterLogEvents</code> call
                    and shows the actual log events — the same <code>queryCrossAccountLogs()</code> call the
                    deployed-app-facing API (<code>/api/aws-logs-api/query</code>) uses, so this also verifies that
                    path end-to-end without needing a real deployed app with an issued key yet. Run &quot;Assume role
                    &amp; list log groups&quot; above first to discover log group names for this environment.
                </p>
                <div style={styles.actions}>
                    <input
                        style={styles.input}
                        placeholder="Log group name, e.g. /ecs/some-service"
                        value={queryLogGroup}
                        onChange={(e) => setQueryLogGroup(e.target.value)}
                        list="aws-logs-known-groups"
                    />
                    <datalist id="aws-logs-known-groups">
                        {(testResult?.logGroups || []).map((lg) => <option key={lg} value={lg} />)}
                    </datalist>
                    <input
                        style={styles.input}
                        placeholder="Filter pattern (optional)"
                        value={queryFilterPattern}
                        onChange={(e) => setQueryFilterPattern(e.target.value)}
                    />
                    <input
                        style={{ ...styles.input, maxWidth: '90px' }}
                        type="number"
                        min="1"
                        max="1000"
                        value={queryLimit}
                        onChange={(e) => setQueryLimit(e.target.value)}
                    />
                    <button
                        type="button"
                        style={styles.btnPrimary}
                        disabled={querying || !selectedEnv || !queryLogGroup}
                        onClick={runQuery}
                    >
                        {querying ? 'Fetching…' : 'Fetch log events'}
                    </button>
                </div>

                {queryError && <div style={styles.error}>{queryError}</div>}

                {queryResult && (
                    <div style={styles.tlsBlock}>
                        <div><strong style={{ color: '#c8e6c9' }}>✅ {queryResult.events.length} event(s)</strong> from <code>{queryResult.logGroupName}</code></div>
                        {queryResult.auditLogError && (
                            <div style={styles.auditWarn}>
                                ⚠️ This attempt was NOT recorded in the audit log: {queryResult.auditLogError}
                            </div>
                        )}
                        <table style={styles.resultsTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Timestamp</th>
                                    <th style={styles.th}>Log stream</th>
                                    <th style={styles.th}>Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {queryResult.events.map((ev, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <tr key={`${ev.timestamp}-${i}`}>
                                        <td style={styles.td}>{ev.timestamp ? new Date(ev.timestamp).toISOString() : ''}</td>
                                        <td style={styles.td}>{ev.logStreamName}</td>
                                        <td style={{ ...styles.td, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px' }}>
                                            {ev.message}
                                        </td>
                                    </tr>
                                ))}
                                {!queryResult.events.length && (
                                    <tr><td style={styles.td} colSpan={3}>No events matched.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>Fetch log data with a CloudWatch Logs Insights query</h3>
                <p style={styles.help}>
                    For callers whose existing log-fetching code already speaks CloudWatch Logs
                    Insights syntax (<code>fields</code> / <code>filter</code> / <code>stats</code>, e.g. code written
                    against <code>StartQuery</code>+<code>GetQueryResults</code>) rather than a simple <code>FilterLogEvents</code>{' '}
                    filter pattern. Same <code>queryCrossAccountLogsInsights()</code> call the deployed-app-facing API
                    (<code>/api/aws-logs-api/insights-query</code>) uses. Insights queries run asynchronously on AWS&apos;s
                    side, so this waits up to 10s for the result.
                </p>
                <div style={styles.actions}>
                    <input
                        style={styles.input}
                        placeholder="Log group name, e.g. /ecs/some-service"
                        value={insightsLogGroup}
                        onChange={(e) => setInsightsLogGroup(e.target.value)}
                        list="aws-logs-known-groups"
                    />
                    <input
                        style={{ ...styles.input, maxWidth: '110px' }}
                        type="number"
                        min="1"
                        max="10080"
                        value={insightsLookbackMinutes}
                        onChange={(e) => setInsightsLookbackMinutes(e.target.value)}
                        title="How many minutes back from now to search"
                    />
                    <span style={styles.help}>minutes back</span>
                    <button
                        type="button"
                        style={styles.btnPrimary}
                        disabled={insightsQuerying || !selectedEnv || !insightsLogGroup}
                        onClick={runInsightsQuery}
                    >
                        {insightsQuerying ? 'Querying…' : 'Run Insights query'}
                    </button>
                </div>
                <textarea
                    style={{ ...styles.input, width: '100%', minHeight: '60px', fontFamily: 'monospace', fontSize: '12px' }}
                    value={insightsQueryString}
                    onChange={(e) => setInsightsQueryString(e.target.value)}
                />

                {insightsError && <div style={styles.error}>{insightsError}</div>}

                {insightsResult && (
                    <div style={styles.tlsBlock}>
                        <div>
                            <strong style={{
                                color: insightsResult.status === 'Complete'
                                    ? '#c8e6c9'
                                    : (INSIGHTS_FAILURE_STATUSES.has(insightsResult.status) ? '#ef9a9a' : '#ffe082'),
                            }}
                            >
                                {insightsResult.status === 'Complete' ? '✅' : (INSIGHTS_FAILURE_STATUSES.has(insightsResult.status) ? '❌' : '⏳')}
                                {' '}{insightsResult.status}
                            </strong>
                            {' '}— {insightsResult.results.length} row(s)
                            {insightsResult.statistics && ` (scanned ${insightsResult.statistics.recordsScanned ?? '?'} record(s), matched ${insightsResult.statistics.recordsMatched ?? '?'})`}
                        </div>
                        {INSIGHTS_RUNNING_STATUSES.has(insightsResult.status) && (
                            <div style={styles.auditWarn}>
                                ⚠️ Query still {insightsResult.status} after the 10s wait — queryId <code>{insightsResult.queryId}</code>.
                                A deployed app can poll <code>/api/aws-logs-api/insights-query-results</code> with this queryId.
                            </div>
                        )}
                        {insightsResult.auditLogError && (
                            <div style={styles.auditWarn}>
                                ⚠️ This attempt was NOT recorded in the audit log: {insightsResult.auditLogError}
                            </div>
                        )}
                        <table style={styles.resultsTable}>
                            <thead>
                                <tr>
                                    {insightsColumns(insightsResult.results).map((field) => (
                                        <th key={field} style={styles.th}>{field}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {insightsResult.results.map((row, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <tr key={i}>
                                        {insightsColumns(insightsResult.results).map((field) => (
                                            <td key={field} style={{ ...styles.td, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px' }}>
                                                {row[field]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {!insightsResult.results.length && (
                                    <tr><td style={styles.td} colSpan={99}>No rows matched.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>Audit log</h3>
                <p style={styles.help}>Every test attempt (success or failure) is recorded here — who ran it, when, and the result.</p>
                {loadingLog ? <p style={styles.help}>Loading…</p> : (
                    <table style={styles.resultsTable}>
                        <thead>
                            <tr>
                                <th style={styles.th}>When</th>
                                <th style={styles.th}>Actor</th>
                                <th style={styles.th}>Environment</th>
                                <th style={styles.th}>Role ARN</th>
                                <th style={styles.th}>Outcome</th>
                                <th style={styles.th}>Message</th>
                            </tr>
                        </thead>
                        <tbody>
                            {log.map((entry) => (
                                <tr key={entry.id} style={entry.outcome === 'error' ? styles.rowFail : undefined}>
                                    <td style={styles.td}>{entry.at}</td>
                                    <td style={styles.td}>{entry.actor}</td>
                                    <td style={styles.td}>{entry.environment}</td>
                                    <td style={styles.td}>{entry.roleArn}</td>
                                    <td style={styles.td}>
                                        <span style={entry.outcome === 'success' ? styles.badgeOk : styles.badgeFail}>
                                            {entry.outcome}
                                        </span>
                                    </td>
                                    <td style={styles.td}>{entry.message}</td>
                                </tr>
                            ))}
                            {!log.length && (
                                <tr><td style={styles.td} colSpan={6}>No test attempts yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <LyricConfirm
                isOpen={confirmRotateApp}
                tone="danger"
                title="Rotate this app's API key?"
                message="The current key stops working immediately. The app must redeploy to pick up the new one — anything using the old key will get 401s until then."
                confirmLabel="Rotate key"
                onConfirm={() => { setConfirmRotateApp(false); appAct('rotate-key'); }}
                onCancel={() => setConfirmRotateApp(false)}
            />
        </div>
    );
}

const styles = {
    page: { padding: '24px', maxWidth: '1100px', margin: '0 auto', color: '#fff' },
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' },
    title: { margin: 0, color: '#4fc3f7' },
    subtitle: { color: '#aaa', marginTop: 0, lineHeight: 1.5 },
    section: { marginTop: '20px', padding: '16px', borderRadius: '6px', background: '#1f2429', border: '1px solid #2f3a44' },
    h3: { marginTop: 0, marginBottom: '8px', color: '#4fc3f7' },
    help: { color: '#888', fontSize: '0.85rem', marginTop: 0 },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '8px' },
    resultsTable: { width: '100%', borderCollapse: 'collapse', marginTop: '8px', fontSize: '13px' },
    th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #2f3a44', color: '#aaa', fontWeight: 600, fontSize: '0.85rem' },
    td: { padding: '6px 8px', verticalAlign: 'top', borderBottom: '1px solid #232a31' },
    rowFail: { background: '#2a1a1a' },
    input: {
        padding: '6px 8px', borderRadius: '4px', border: '1px solid #444',
        background: '#161a1e', color: '#fff', width: '100%', boxSizing: 'border-box', fontSize: '13px',
    },
    btn: { padding: '8px 14px', background: '#2f3a44', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    btnPrimary: { padding: '8px 14px', background: '#0052cc', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 },
    btnDanger: { padding: '6px 10px', background: '#5a1a1a', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' },
    actions: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', flexWrap: 'wrap' },
    error: { padding: '8px 12px', background: '#4a1a1a', border: '1px solid #ef5350', borderRadius: '4px', color: '#ffcdd2', margin: '8px 0' },
    errInline: { color: '#ef9a9a', fontSize: '12px' },
    auditWarn: {
        marginTop: '8px', padding: '6px 10px', background: '#4a3a1a', border: '1px solid #ffb74d',
        borderRadius: '4px', color: '#ffe0b2', fontSize: '12px',
    },
    badgeOk: { display: 'inline-block', padding: '2px 8px', background: '#1a4a1a', color: '#c8e6c9', border: '1px solid #4caf50', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
    badgeFail: { display: 'inline-block', padding: '2px 8px', background: '#4a1a1a', color: '#ffcdd2', border: '1px solid #ef5350', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
    tlsBlock: { marginTop: '12px', padding: '10px 12px', background: '#0d1117', border: '1px solid #2f3a44', borderRadius: '4px', fontSize: '13px' },
    codeInline: { padding: '4px 8px', background: '#161a1e', border: '1px solid #2f3a44', borderRadius: '4px', fontSize: '12px', wordBreak: 'break-all' },
    pre: {
        marginTop: '6px', padding: '10px 12px', background: '#161a1e', border: '1px solid #2f3a44',
        borderRadius: '4px', fontSize: '12px', overflowX: 'auto', whiteSpace: 'pre',
    },
};
